import React from "react";
import { StyleSheet, View, Image } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";

export interface ICustomCardFieldRenderOptions {
  fieldKey: string;
  idx: number;
  item: Record<string, any>;
  title: string;
  dateStr: string;
  photoUrl?: string;
  description: string;
  email: string;
  textColor: string;
  textMuted: string;
}

/**
 * Renders an individual field defined in activeConfig.fields for ThemedCustomCard.
 */
export function renderCustomCardField({
  fieldKey,
  idx,
  item,
  title,
  dateStr,
  photoUrl,
  description,
  email,
  textColor,
  textMuted,
}: ICustomCardFieldRenderOptions): React.ReactNode {
  const uniqueKey = `custom-field-${fieldKey}-${idx}`;
  switch (fieldKey) {
    case "title":
    case "name":
    case "displayName":
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} type="defaultSemiBold" style={styles.titleText}>
          {title}
        </ThemedText>
      );

    case "date": {
      const effectiveDateStr =
        item.formattedDate ||
        item.dateText ||
        (typeof item.date === "object" ? item.date.formatted || item.date.from : item.date) ||
        dateStr;
      if (!effectiveDateStr) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.dateText, { color: textMuted }]}>
          📅 {effectiveDateStr}
        </ThemedText>
      );
    }

    case "photo":
    case "thumbnail":
      if (!photoUrl) return null;
      return (
        <Image
          key={uniqueKey}
          source={{ uri: photoUrl }}
          style={styles.fieldThumbnail}
          resizeMode="cover"
        />
      );

    case "description":
      if (!description) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={2} style={[styles.descText, { color: textColor }]}>
          {description}
        </ThemedText>
      );

    case "email":
      if (!email) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.emailText, { color: textMuted }]}>
          ✉ {email}
        </ThemedText>
      );

    case "address": {
      const addr = item.address;
      if (!addr) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          📍 {addr}
        </ThemedText>
      );
    }

    case "location":
    case "coordinates": {
      if (item.latitude === undefined || item.longitude === undefined) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          🌐 {item.latitude.toFixed(4)}, {item.longitude.toFixed(4)}
        </ThemedText>
      );
    }

    case "ownerDisplayName":
    case "owner": {
      const ownerName = item.ownerDisplayName || item.addedByName || (item.isOwner ? "You" : null);
      if (!ownerName) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          👤 {ownerName}
        </ThemedText>
      );
    }

    case "tagType":
    case "category": {
      const tagCategory = item.tagType || item.category;
      if (!tagCategory) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          🏷 Category: {tagCategory}
        </ThemedText>
      );
    }

    case "role":
    case "permission": {
      const userRole = item.role || item.permission || item.userRole;
      if (!userRole) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          🔒 Role: {userRole}
        </ThemedText>
      );
    }

    case "storageQuota":
    case "quota": {
      const quotaBytes = item.storageQuota ?? item.quotaBytes;
      if (quotaBytes === undefined) return null;
      const gb = (quotaBytes / (1024 * 1024 * 1024)).toFixed(1);
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          💾 Quota: {gb} GB
        </ThemedText>
      );
    }

    case "userType":
    case "accountType": {
      const uType = item.userType || item.accountType;
      if (!uType) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          ⚙ Account: {uType}
        </ThemedText>
      );
    }

    case "authProvider": {
      const auth = item.authProvider;
      if (!auth) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          🔑 IDP: {auth === "google" ? "Google" : "Password"}
        </ThemedText>
      );
    }

    case "recipient":
    case "recipientName":
    case "recipientDisplayName": {
      const recipient = item.recipientDisplayName || item.recipientName || item.recipient;
      if (!recipient) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          👤 Recipient: {recipient}
        </ThemedText>
      );
    }

    case "subject": {
      const subjectText = item.subject || item.title;
      if (!subjectText) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} type="defaultSemiBold" style={styles.titleText}>
          {subjectText}
        </ThemedText>
      );
    }

    case "sender":
    case "senderDisplayName": {
      const sender = item.senderDisplayName || item.senderName || item.senderEmail;
      if (!sender) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          ✉ From: {sender}
        </ThemedText>
      );
    }

    case "messageType": {
      const mType = item.messageType || item.type;
      if (!mType) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          🏷 Type: {mType}
        </ThemedText>
      );
    }

    case "unreadStatus": {
      const isUnread = item.isRead === false || item.unread === true;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: isUnread ? "#D97706" : textMuted }]}>
          {isUnread ? "🟡 Unread" : "⚪ Read"}
        </ThemedText>
      );
    }

    case "sharedItem": {
      const sharedItem = item.sharedItem;
      if (!sharedItem) return null;
      return (
        <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
          🔗 Shared: {sharedItem.title || sharedItem.name || "Item"}
        </ThemedText>
      );
    }

    case "favouriteRating":
      return (
        <View key={uniqueKey} style={styles.ratingRow}>
          {[1, 2, 3, 4, 5].map((starIdx) => (
            <Ionicons
              key={starIdx}
              name={starIdx <= (item.favouriteRating || 3) ? "star" : "star-outline"}
              size={12}
              color={starIdx <= (item.favouriteRating || 3) ? "#EAB308" : textMuted}
            />
          ))}
        </View>
      );

    default:
      if (item[fieldKey] !== undefined && typeof item[fieldKey] !== "object") {
        return (
          <ThemedText key={uniqueKey} numberOfLines={1} style={[styles.metaFieldText, { color: textMuted }]}>
            {String(item[fieldKey])}
          </ThemedText>
        );
      }
      return null;
  }
}

const styles = StyleSheet.create({
  titleText: {
    fontSize: 14,
    lineHeight: 20,
  },
  dateText: {
    fontSize: 11,
  },
  emailText: {
    fontSize: 11,
  },
  descText: {
    fontSize: 12,
    lineHeight: 17,
  },
  fieldThumbnail: {
    width: 44,
    height: 44,
    borderRadius: 4,
    marginVertical: 4,
  },
  ratingRow: {
    flexDirection: "row",
    gap: 2,
    marginVertical: 2,
  },
  metaFieldText: {
    fontSize: 11,
  },
});
